import { CSSProperties, ElementType } from 'react'
import clsx from 'clsx'
import {
  PagebuilderHeadingSizeOptions,
  PagebuilderHeadingTagOptions
} from 'cms/constants/pagebuilder'

import RichTextLite from 'lib/sanity/richText/RichTextLite'
import { FontSizeOptions, getTextStyle } from 'shared/ui/utils/designTokens'

import {
  defaultPagebuilderAlign,
  mapPagebuilderAlignToJustifyContent
} from '../mappers'
import { PagebuilderHeadingViewModel } from '../types'
import { resolveMaxContentWidth } from '../utils'

import styles from './Heading.module.scss'

interface HeadingProps {
  heading?: PagebuilderHeadingViewModel
  className?: string
}

const defaultDesktopSize = FontSizeOptions['4xl']
const defaultMobileSize = FontSizeOptions['2xl']

const parseTextStyle = (textStyle: string) => {
  const fontSize = textStyle.match(/font-size:\s*([^;]+)/)?.[1]?.trim()
  const lineHeight = textStyle.match(/line-height:\s*([^;]+)/)?.[1]?.trim()

  return { fontSize, lineHeight }
}

const resolveFontSize = (
  mode: PagebuilderHeadingSizeOptions | undefined,
  customValue: number | undefined,
  fallbackToken: FontSizeOptions
) => {
  if (mode === PagebuilderHeadingSizeOptions.custom && customValue) {
    return parseTextStyle(`font-size: ${customValue}px; line-height: 1.2;`)
  }

  if (typeof mode === 'string' && mode in FontSizeOptions) {
    return parseTextStyle(getTextStyle(mode as FontSizeOptions, 120))
  }

  return parseTextStyle(getTextStyle(fallbackToken, 120))
}

const Heading = ({ heading, className }: HeadingProps) => {
  if (!heading) return null

  if (!heading.content) return null

  const Tag: ElementType = heading.tag ?? PagebuilderHeadingTagOptions.h2

  const desktopTextStyle = resolveFontSize(
    heading.sizeDesktopMode,
    heading.sizeDesktopCustomValue,
    defaultDesktopSize
  )
  const mobileTextStyle = resolveFontSize(
    heading.sizeMobileMode,
    heading.sizeMobileCustomValue,
    defaultMobileSize
  )
  const desktopAlign = heading.alignDesktop ?? defaultPagebuilderAlign
  const mobileAlign = heading.alignMobile ?? defaultPagebuilderAlign
  const maxContentWidth = resolveMaxContentWidth(heading)

  const wrapperStyle = {
    '--desktop-align-items': mapPagebuilderAlignToJustifyContent(desktopAlign),
    '--desktop-text-align': desktopAlign,
    '--mobile-align-items': mapPagebuilderAlignToJustifyContent(mobileAlign),
    '--mobile-text-align': mobileAlign,
    '--desktop-font-size': desktopTextStyle.fontSize,
    '--desktop-line-height': desktopTextStyle.lineHeight,
    '--mobile-font-size': mobileTextStyle.fontSize,
    '--mobile-line-height': mobileTextStyle.lineHeight,
    '--max-content-width': maxContentWidth
  } as CSSProperties

  return (
    <Tag
      className={clsx(styles.headingWrapper, className)}
      style={wrapperStyle}
    >
      <div className={styles.contentWrapper}>
        <RichTextLite value={heading.content} />
      </div>
    </Tag>
  )
}

export default Heading
